import { Component, Children, createContext, PureComponent, FC, ReactNode, MouseEventHandler, ComponentProps } from 'react'
import * as cn from 'classnames'
import * as isEqual from 'lodash/isEqual'
import * as get from 'lodash/get'
import * as isNumber from 'lodash/isNumber'
import * as omit from 'lodash/omit'
import * as styles from 'styles/molecules/tables/Table2.css'
import Paginator from 'components/molecules/tables/Paginator'
import PageSizeSelector from 'components/atoms/tables/PageSizeSelector'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { BaseSorting, Editable, SortingDirection } from 'interfaces/table'
import { SortablePrimitive } from 'components/molecules/tables/Table'
import { getRowProps, isEmptyFormRow, isFormRow } from 'helpers/tableHelpers'
import { isElementOfType } from 'tangle-shared'
import withContext from 'hocs/withContext/withContext'
import { shallowEqual } from 'tangle-shared'
import { QAMarked } from 'interfaces/component'

type AddRowSpanFuncParam<TEntity> = {
  name: string
  selector: SortingSelector<TEntity>
  breakPredicate?: Predicate<TEntity>
  force?: boolean
}

type Predicate<TEntity> = (props: TEntity) => boolean
type SortingSelector<TEntity> = (props: TEntity) => SortablePrimitive | null | undefined
type AddSortableColumnFunc<TEntity> = (name: string, selector?: SortingSelector<TEntity>) => void
type AddRowSpanFunc<TEntity> = (param: AddRowSpanFuncParam<TEntity>) => void
type RemoveRowSpanFunc = (name: string) => void
type GetRowKeyFunc<TEntity> = (props: TEntity) => string
type OnChangeSortingFunc = (sorting: BaseSorting) => void

type RowSpanGrid = Record<
  string, // column name
  Record<
    string, // row key calculated with `getRowKey`
    number  // actual span number
  >
>

export type TableContextType<TEntity = {}> = {
  sortableColumns: Record<string, SortableColumnDefinition<TEntity>>
  sorting?: BaseSorting
  rows: TEntity[]
  rowSpanGrid: RowSpanGrid | null
  getRowKey: GetRowKeyFunc<TEntity>

  onChangeSorting: OnChangeSortingFunc
  addSortableColumn: AddSortableColumnFunc<TEntity>
  addRowSpan: AddRowSpanFunc<TEntity>
  removeRowSpan: RemoveRowSpanFunc
}

export const TableContext = createContext<TableContextType<any>>({
  sortableColumns: {},
  rows: [],
  rowSpanGrid: null,
  getRowKey: () => '',
  onChangeSorting: () => { /* do nothing */},
  addSortableColumn: () => { /* do nothing */},
  addRowSpan: () => { /* do nothing */},
  removeRowSpan: () => { /* do nothing */},
})

// TODO: server, client
// TODO: styling


// HeadRowMarker
class HeadRowMarker extends Component {
  render () {
    return this.props.children
  }
}


// HeadRow
type HeadRowProps = {
  theme?: 'white'
  className?: string
}

class HeadRow extends Component<HeadRowProps> {
  render () {
    const { children, theme, className } = this.props

    const childrenArray = Children.toArray(children)
    const areValidChildren = childrenArray.every(
      (element) => isElementOfType(element, HeadCell),
    )

    if (!areValidChildren) {
      return null
    }

    if (!childrenArray.length) {
      return null
    }

    return (
      <tr className={ cn(styles.headerRow, theme && styles[theme], className) }>
        { children }
      </tr>
    )
  }
}


// HeadCell
type HeadCellProps<TEntity> = {
  name: string
  sorting?: boolean | SortingSelector<TEntity>
  table: TableContextType<TEntity>
  className?: string
}

class HeadCellInner<TEntity> extends Component<HeadCellProps<TEntity>> {
  render () {
    const { name, children, className: classNameOuter } = this.props
    const { sortableColumns } = this.props.table

    const sortableColumn = sortableColumns[name]
    const isColumnSortable = Boolean(sortableColumn)

    const className = cn(styles.headerCell, {
      [styles.sortableHeaderCell]: isColumnSortable,
    }, classNameOuter)

    const onChangeSorting = isColumnSortable ? this.onChangeSorting : undefined

    return (
      <th
        className={ className }
        onClick={ onChangeSorting }
      >
        <span className={ styles.headerTitle }>{ children }</span>
        { this.getSortingElement() }
      </th>
    )
  }

  componentDidMount () {
    const { name, sorting } = this.props
    if (sorting) {
      this.props.table.addSortableColumn(name, typeof sorting === 'function' ? sorting : undefined)
    }
  }

  private getSortingElementClasses = (): {} | null => {
    const { sorting } = this.props.table
    if (!sorting || sorting.columnName !== this.props.name) {
      return null
    }

    return {
      [styles.up]: sorting.direction === 'asc',
      [styles.down]: sorting.direction === 'desc',
    }
  }

  private getSortingElement = (): ReactNode => {
    const { sortableColumns } = this.props.table
    const sortableColumn = sortableColumns[this.props.name]

    if (!sortableColumn) {
      return null
    }

    return (
      <div className={ cn(styles.sortingWrapper, this.getSortingElementClasses()) } />
    )
  }

  private onChangeSorting: MouseEventHandler<HTMLTableHeaderCellElement> = () => {
    const { name, table } = this.props
    const { sorting: contextSorting } = table

    const direction = contextSorting && contextSorting.columnName === name
      ? (contextSorting.direction === 'desc' ? 'asc' : 'desc')
      : 'asc'

    table.onChangeSorting({
      columnName: name,
      direction,
    })
  }
}

const HeadCell = withContext(TableContext, 'table')(HeadCellInner)


// RowSpan
type RowSpanProps<TEntity> = {
  table: TableContextType<TEntity>
  name: string
  /* `breakPredicate` returning boolean breaks a sequence of rowspans if returns true
      needed for editing transaction if it's cell is spanned
   */
  breakPredicate?: (entity: TEntity) => boolean
  // use this span definition even if a table is not sorted using corresponding column
  force?: boolean
  children: (entity: TEntity) => SortablePrimitive
}

class RowSpanInner<TEntity> extends PureComponent<RowSpanProps<TEntity>> {
  render () {
    return null
  }

  componentDidMount () {
    this.registerRowSpan()
  }

  componentDidUpdate (prevProps: RowSpanProps<TEntity>) {
    const { children, table, ...restProps } = this.props
    const { children: prevChildren, table: prevTable, ...prevRestProps } = prevProps
    if (shallowEqual(restProps, prevRestProps)) {
      return
    }

    this.registerRowSpan()
  }

  componentWillUnmount () {
    this.props.table.removeRowSpan(this.props.name)
  }

  private registerRowSpan = () => {
    const { children, table, ...restProps } = this.props
    this.props.table.addRowSpan({
      ...restProps,
      selector: children,
    })
  }
}

const RowSpan = withContext(TableContext, 'table')(RowSpanInner)


// RowTemplate
type RowTemplateProps<TEntity extends {}> = {
  table: TableContextType<TEntity>
  children: (row: TEntity) => ReactNode
}

class RowTemplateInner<TEntity extends {}> extends Component<RowTemplateProps<TEntity>> {
  render () {
    const { children, table } = this.props
    const { rows } = table

    return rows.map(children)
  }
}

const RowTemplate = withContext(TableContext, 'table')(RowTemplateInner)


// Rows
class Rows extends Component {
  render () {
    const { children } = this.props
    return children
  }
}


// Row
type RowProps = {
  className?: string
}

const Row: FC<RowProps> = (props) => {
  const { className, children } = props

  if (!children || !Children.count(children)) {
    return null
  }

  return (
    <tr className={ cn(styles.row, className) }>
      { children }
    </tr>
  )
}


// Cell
type CellProps = QAMarked & {
  table: TableContextType
  rowKey: string
  name: string
  className?: string
}

class CellInner extends Component<CellProps> {
  render () {
    const { name, rowKey, children, table, className, qaMarker } = this.props
    const { rowSpanGrid } = table

    const rowSpan = get(rowSpanGrid, `${ name }.${ rowKey }`)
    if (rowSpan === -1) {
      return <td className={ cn(styles.hidden, className) } data-qa-marker={ qaMarker } data-qa-type='hidden' />
    }

    return (
      <td
        className={ cn(styles.cell, className) }
        rowSpan={ isNumber(rowSpan) && rowSpan > 1 ? rowSpan : undefined }
        data-qa-marker={ qaMarker }
      >
        { children }
      </td>
    )
  }
}

const Cell = withContext(TableContext, 'table')(CellInner)

// Cell simple

class CellSimpleStyles extends Component<ComponentProps<typeof Cell>> {
  render () {
    const { className } = this.props

    return (
      <Cell { ...this.props } className={ cn(styles.cellSimpleStyles, className) } />
    )
  }
}

// Table2
type Table2Props<TEntity extends {}> = {
  sorting?: BaseSorting
  isLoading?: boolean
  className?: string
  rows: TEntity[]
  getRowKey: GetRowKeyFunc<TEntity>

  onChangeSorting?: (sorting: BaseSorting) => void
}

type SortableColumnDefinition<TEntity> = {
  name: string
  selector?: SortingSelector<TEntity>
}

type RowSpanDefinition<TEntity> = {
  name: string
  selector: SortingSelector<TEntity>
  force?: boolean
  breakPredicate?: Predicate<TEntity>
}

type Table2State<TEntity> = {
  prevSorting?: BaseSorting
  sorting?: BaseSorting

  rowSpansDefinition: Record<string, RowSpanDefinition<TEntity>>
  sortableColumnsDefinition: Record<string, SortableColumnDefinition<TEntity>>
}

/**
 * @deprecated Too old and bulky
 *
 */
class Table2<TEntity> extends PureComponent<Table2Props<TEntity>, Table2State<TEntity>> {
  static HeadRowMarker = HeadRowMarker
  static HeadRow = HeadRow
  static HeadCell = HeadCell
  static Rows = Rows
  static Row = Row
  static RowTemplate = RowTemplate
  static RowSpan = RowSpan
  static Cell = Cell
  static CellSimpleStyles = CellSimpleStyles
  static Paginator = Paginator
  static PageSizeSelector = PageSizeSelector

  /**
   * For now only `number` and `string` types are supported as an output of `sortSelector`.
   * It's used inside the `Array.prototype.sort` function.
   */
  static compare<TProps> (
    direction: SortingDirection,
    selector: SortingSelector<TProps>,
    rawA: Editable<TProps>,
    rawB: Editable<TProps>
  ): number {

    // TODO: eliminate empty form rows
    const aIsEmptyForm = isEmptyFormRow(rawA)
    const bIsEmptyForm = isEmptyFormRow(rawB)
    if (aIsEmptyForm || bIsEmptyForm) {
      // the blank form always should be the first row
      return aIsEmptyForm ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY
    }

    const aIsForm = isFormRow(rawA)
    const bIsForm = isFormRow(rawB)
    if (aIsForm || bIsForm) {
      // if `a` is a form of props and `b` is a `props` itself they should be nearby
      // that is why I'm operating with `1`
      if (aIsForm && !bIsForm && getRowProps(rawA) === rawB) {
        return 1
      }

      if (bIsForm && !aIsForm && getRowProps(rawB) === rawA) {
        return -1
      }
    }

    // as TEntity because it's not form row
    // passing form rows here prevented by upper code
    const a = selector(getRowProps(rawA) as TProps)
    const b = selector(getRowProps(rawB) as TProps)

    let comparison = 0

    // `undefined`s and `null`s should be the first rows in asc
    if (a === undefined || b === undefined) {
      comparison = a === b ? 0 : (a === undefined ? -2000 : 2000)
    } else if (a === null || b === null) {
      comparison = a === b ? 0 : (a === null ? -1000 : 1000)
    }
    if (typeof a === 'string' && typeof b === 'string') {
      comparison = a.localeCompare(b)
    }
    if (typeof a === 'number' && typeof b === 'number') {
      comparison = a < b ? -1000 : (a > b ? 1000 : 0)
    }

    return comparison * (direction === 'asc' ? 1 : -1)
  }

  static getDerivedStateFromProps (nextProps: Table2Props<any>, prevState: Table2State<any>) {
    const { sorting } = nextProps
    if (sorting === prevState.prevSorting || shallowEqual(sorting, prevState.sorting)) {
      return {}
    }
    return { sorting, prevSorting: sorting }
  }

  state: Table2State<TEntity> = {
    prevSorting: this.props.sorting,
    sorting: this.props.sorting,
    rowSpansDefinition: {},
    sortableColumnsDefinition: {},
  }

  private didMount = false
  private registeredSortableColumns: Table2State<TEntity>['sortableColumnsDefinition'] = {}
  private registeredRowSpans: Record<string, RowSpanDefinition<TEntity>> = {}

  render () {
    const { children, className } = this.props

    const childrenArray = Children.toArray(children)
    const headRow = childrenArray.find((element) => isElementOfType(element, HeadRowMarker))
    const rows = childrenArray.find((element) => isElementOfType(element, Rows))

    const head = headRow ? (
      <thead>
        { headRow }
      </thead>
    ) : null

    const body = (
      <tbody>
        { rows }
      </tbody>
    )

    return (
      <div className={ cn(styles.tableContainer, className) }>
        <TableContext.Provider value={ this.getContextValue() }>
          <table className={ styles.table }>
            { head }
            { body }
          </table>
          { this.renderLoadingOverlay() }
        </TableContext.Provider>
      </div>
    )
  }

  renderLoadingOverlay = () => {
    return this.props.isLoading ? <LoadingOverlay /> : null
  }

  componentDidMount () {
    this.didMount = true

    this.setState({
      rowSpansDefinition: this.registeredRowSpans,
      sortableColumnsDefinition: this.registeredSortableColumns,
    })
  }

  calculateRowSpans = (rows: TEntity[]): Record<string, Record<string, number>> | null => {
    const { rowSpansDefinition, sorting } = this.state

    if (!rows.length || !Object.keys(this.state.rowSpansDefinition).length) {
      return null
    }

    let rowSpans: Record<string, Record<string, number>> = {}
    const { getRowKey } = this.props

    Object.keys(rowSpansDefinition)
      .forEach((name) => {
        const { selector, breakPredicate } = rowSpansDefinition[name]

        // prevent collapsing if it's not the same column which is used for sorting if the span is not forced
        if (sorting && sorting.columnName !== name) {
          return
        }

        /**
         * I need to calculate raw spans for cells if actual props in adjacent cells are the same
         * and `collapseSame` option is passed for a column.
         *
         * I traverse through the rows in a reverse order and count the same cells
         * then dump this value to a dictionary for each column.
         *
         * `-1` means the specific cell shouldn't be printed
         * `1` means a common row span
         * Other values mean specific row span value
         *
         * See the final `RowSpanGrid` structure:
         * type RowSpanGrid = Record<
         *   string, // column name
         *   Record<
         *     string, // row key calculated with `getRowKey`
         *     number  // actual span number
         *   >
         * >
         */

        let rowSpanCount = 1
        let rowValue = selector(rows[rows.length - 1])
        let i = rows.length
        const spanDict: Record<string, number> = {}
        while (i--) {
          const currentRow = rows[i]
          const currentRowKey = getRowKey(currentRow)
          const prevValue = i === 0 ? null : selector(rows[i - 1])
          const prevBreakRowSpan = i !== 0 && breakPredicate && breakPredicate(rows[i - 1])
          const currentBreakRowSpan = breakPredicate && breakPredicate(currentRow)

          if (!isEqual(rowValue, prevValue) || prevBreakRowSpan || currentBreakRowSpan) {
            rowValue = prevValue
            spanDict[currentRowKey] = rowSpanCount
            rowSpanCount = 1
          } else {
            rowSpanCount++
            spanDict[currentRowKey] = -1
          }
        }

        rowSpans = {
          ...rowSpans,
          [name]: spanDict,
        }
      })

    return rowSpans
  }

  private getContextValue (): TableContextType {
    let rows = this.props.rows
    const { sorting, sortableColumnsDefinition } = this.state

    if (sorting) {
      const columnDefinition = sortableColumnsDefinition[sorting.columnName]
      if (columnDefinition && columnDefinition.selector) {
        const { selector } = columnDefinition
        // TODO get rid off ! in "row.__props!"
        const editableSortingSelector = (row: Editable<TEntity>): SortablePrimitive | null | undefined =>
          isFormRow(row) ? selector(row.__props!) : selector(row)

        rows = [...rows].sort((a: TEntity, b: TEntity): number =>
          Table2.compare(sorting.direction, editableSortingSelector, a, b))
      }
    }

    const rowSpanGrid = this.calculateRowSpans(rows)

    return {
      sorting: this.state.sorting,
      sortableColumns: this.state.sortableColumnsDefinition,

      rows,
      rowSpanGrid,
      getRowKey: this.props.getRowKey,

      onChangeSorting: this.onChangeSorting,
      addRowSpan: this.addRowSpan,
      removeRowSpan: this.removeRowSpan,
      addSortableColumn: this.addSortableColumn,
    }
  }

  private addSortableColumn: AddSortableColumnFunc<TEntity> = (name, selector) => {
    this.registeredSortableColumns = {
      ...this.registeredSortableColumns,
      [name]: { name, selector },
    }
  }

  private addRowSpan: AddRowSpanFunc<TEntity> = (rowSpan) => {
    const { name } = rowSpan

    this.registeredRowSpans = {
      ...this.registeredRowSpans,
      [name]: rowSpan,
    }

    if (this.didMount) {
      this.setState({
        rowSpansDefinition: this.registeredRowSpans,
      })
    }
  }

  private removeRowSpan: RemoveRowSpanFunc = (name) => {
    this.registeredRowSpans = omit(this.registeredRowSpans, name)

    if (this.didMount) {
      this.setState({
        rowSpansDefinition: this.registeredRowSpans,
      })
    }
  }

  private onChangeSorting: OnChangeSortingFunc = (sorting) => {
    const { columnName } = sorting
    const { onChangeSorting } = this.props
    const sortableColumn = this.state.sortableColumnsDefinition[columnName]

    if (onChangeSorting) {
      onChangeSorting(sorting)
      return
    }

    if (sortableColumn && sortableColumn.selector) {
      this.setState({
        sorting,
      })
      return
    }
  }
}

export default Table2
